import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ProductCard from '@/components/feature/ProductCard';
import { openProductQuoteOnWhatsApp } from '@/utils/whatsapp';
import {
  fetchAtas,
  fetchProdutoCategorias,
  fetchProdutos,
  type Ata,
  type Produto,
  type ProdutoCategoria,
} from '@/services/catalog';

interface QuoteVariant {
  id: number;
  cor: string;
  tamanho: string;
  sku: string;
  estoque: number;
  ativo: boolean;
}

export default function ProductsSection() {
  const [startIndex, setStartIndex] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(6);
  const [allProducts, setAllProducts] = useState<Produto[]>([]);
  const [atas, setAtas] = useState<Ata[]>([]);
  const [categorias, setCategorias] = useState<ProdutoCategoria[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setItemsPerView(1);
      } else if (window.innerWidth < 1024) {
        setItemsPerView(2);
      } else if (window.innerWidth < 1280) {
        setItemsPerView(3);
      } else {
        setItemsPerView(6);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    Promise.all([fetchProdutos(), fetchAtas(), fetchProdutoCategorias()]).then(([productsData, atasData, categoriesData]) => {
      setAllProducts(productsData);
      setAtas(atasData);
      setCategorias(categoriesData);
    });
  }, []);

  const products = allProducts.filter((p) => p.ativo).slice(0, 8);

  useEffect(() => {
    if (products.length <= 1) return;

    const intervalId = window.setInterval(() => {
      setStartIndex((prev) => (prev + 1) % products.length);
    }, 4000);

    return () => window.clearInterval(intervalId);
  }, [products.length]);

  useEffect(() => {
    if (products.length === 0) {
      setStartIndex(0);
      return;
    }

    setStartIndex((prev) => prev % products.length);
  }, [products.length]);

  const getCategoryName = (catId: number) =>
    categorias.find((c) => c.id === catId)?.nome || '';

  const getAtasNomes = (atasIds: number[]) =>
    atasIds
      .map((id) => atas.find((a) => a.id === id))
      .filter(Boolean)
      .map((a) => a!.numero);

  const handleQuote = (
    product: Produto,
    variant: QuoteVariant | undefined,
    quantity: number,
  ) => {
    openProductQuoteOnWhatsApp({
      productName: product.nome,
      productSku: product.sku,
      brand: product.marca,
      variant,
      quantity,
      atasNomes: getAtasNomes(product.atasIds),
    });
  };

  const nextSlide = () => {
    if (products.length === 0) return;
    setStartIndex((prev) => (prev + 1) % products.length);
  };

  const prevSlide = () => {
    if (products.length === 0) return;
    setStartIndex((prev) => (prev - 1 + products.length) % products.length);
  };

  const visibleItemsCount = products.length > 0 ? Math.min(itemsPerView, products.length) : 0;
  const visibleProducts = [];
  for (let i = 0; i < visibleItemsCount; i++) {
    visibleProducts.push(products[(startIndex + i) % products.length]);
  }

  const gridClass =
    visibleItemsCount <= 1 ? 'grid-cols-1'
      : visibleItemsCount === 2 ? 'grid-cols-2'
        : visibleItemsCount === 3 ? 'grid-cols-3'
          : visibleItemsCount === 4 ? 'grid-cols-4'
            : visibleItemsCount === 5 ? 'grid-cols-5'
              : 'grid-cols-6';

  return (
    <section id="produtos" className="overflow-hidden bg-white py-8 sm:py-10 md:py-12">
      <div className="w-full px-4 sm:px-6 md:px-8 lg:px-12">
        <div className="relative mb-8 text-center md:mb-10">
          <h2 className="mb-2 text-xs font-extrabold uppercase tracking-wider text-donnos-green sm:text-sm">
            PRODUTOS EM DESTAQUE
          </h2>
          <h3 className="text-xl font-extrabold text-gray-950 sm:text-2xl md:text-3xl lg:text-4xl">
            Equipamentos com{' '}
            <span className="text-donnos-green">qualidade e garantia</span>
          </h3>
          <p className="mt-2 text-sm text-slate-500">
            Selecionamos os melhores produtos das principais marcas para impulsionar o seu negócio.
          </p>

          <button
            onClick={() => navigate('/produtos')}
            className="mt-5 inline-flex items-center gap-2 rounded-md border border-donnos-green/30 px-5 py-2 text-xs font-bold text-donnos-green transition-colors hover:bg-donnos-green hover:text-white md:absolute md:right-0 md:top-1 md:mt-0"
          >
            Ver todos os produtos
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>

        <div className="relative">
          <button
            onClick={prevSlide}
            className="absolute left-2 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-gray-950 text-white transition-colors hover:bg-donnos-green md:flex"
            aria-label="Anterior"
          >
            <i className="ri-arrow-left-line"></i>
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-2 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-gray-950 text-white transition-colors hover:bg-donnos-green md:flex"
            aria-label="Próximo"
          >
            <i className="ri-arrow-right-line"></i>
          </button>

          <div className={`grid ${gridClass} gap-4`}>
            {visibleProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                categoryNames={product.categorias.map((c) => getCategoryName(c))}
                atasNomes={getAtasNomes(product.atasIds)}
                onQuote={(variant, quantity) => handleQuote(product, variant, quantity)}
              />
            ))}
          </div>
        </div>

        <div className="mt-5 flex items-center justify-center gap-3 md:hidden">
          <button
            onClick={prevSlide}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-950 text-white transition-colors hover:bg-donnos-green"
            aria-label="Anterior"
          >
            <i className="ri-arrow-left-line"></i>
          </button>
          <button
            onClick={nextSlide}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-950 text-white transition-colors hover:bg-donnos-green"
            aria-label="Próximo"
          >
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </div>
    </section>
  );
}
